@import "./reset.css";

html {
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  min-width: 300px;
  overflow-x: hidden;
  overflow-y: scroll;
  text-rendering: optimizeLegibility;

  height: 100%;
  font-size: 16px;
  user-select: none;
  -webkit-user-select: none;
  cursor: default;
  color: var(--color-text);
  background-color: var(--color-background);
}

article,
aside,
figure,
footer,
header,
hgroup,
section {
  display: block;
}

body,
button,
input,
select,
textarea {
  font-family:
    BlinkMacSystemFont,
    -apple-system,
    "Segoe UI",
    "Roboto",
    "Oxygen",
    "Ubuntu",
    "Cantarell",
    "Fira Sans",
    "Droid Sans",
    "Helvetica Neue",
    "Helvetica",
    "Arial",
    sans-serif;
}

code,
pre {
  -moz-osx-font-smoothing: auto;
  -webkit-font-smoothing: auto;
  font-family: monospace;
}

body {
  color: var(--color-text);
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  height: 100%;
}

a {
  color: inherit;
  text-decoration: none;
}

hr {
  background-color: var(--color-border);
  border: none;
  display: block;
  height: 1px;
  margin: 1.5rem 0;
}

img {
  height: auto;
  max-width: 100%;
}

input[type="checkbox"],
input[type="radio"] {
  vertical-align: baseline;
}

small {
  font-size: 0.875em;
}

span {
  font-style: inherit;
  font-weight: inherit;
}

strong {
  font-weight: 700;
}

// Block
table {
  width: 100%;
  td,
  th {
    text-align: left;
    vertical-align: top;
  }
}

// NOTE:
// workaround for broken backdrop blur utils in tailwind
// from: https://github.com/tailwindlabs/tailwindcss/discussions/7044#discussioncomment-4697891
:root {
  --empty: ;
}
*,
:after,
:before {
  --tw-translate-x: 0;
  --tw-translate-y: 0;
  --tw-rotate: 0;
  --tw-skew-x: 0;
  --tw-skew-y: 0;
  --tw-scale-x: 1;
  --tw-scale-y: 1;
  --tw-pan-x: var(--empty,);
  --tw-pan-y: var(--empty,);
  --tw-pinch-zoom: var(--empty,);
  --tw-scroll-snap-strictness: proximity;
  --tw-ordinal: var(--empty,);
  --tw-slashed-zero: var(--empty,);
  --tw-numeric-figure: var(--empty,);
  --tw-numeric-spacing: var(--empty,);
  --tw-numeric-fraction: var(--empty,);
  --tw-ring-inset: var(--empty,);
  --tw-ring-offset-width: 0px;
  --tw-ring-offset-color: #fff;
  --tw-ring-color: rgba(59, 130, 246, 0.5);
  --tw-ring-offset-shadow: 0 0 #0000;
  --tw-ring-shadow: 0 0 #0000;
  --tw-shadow: 0 0 #0000;
  --tw-shadow-colored: 0 0 #0000;
  --tw-blur: var(--empty,);
  --tw-brightness: var(--empty,);
  --tw-contrast: var(--empty,);
  --tw-grayscale: var(--empty,);
  --tw-hue-rotate: var(--empty,);
  --tw-invert: var(--empty,);
  --tw-saturate: var(--empty,);
  --tw-sepia: var(--empty,);
  --tw-drop-shadow: var(--empty,);
  --tw-backdrop-blur: var(--empty,);
  --tw-backdrop-brightness: var(--empty,);
  --tw-backdrop-contrast: var(--empty,);
  --tw-backdrop-grayscale: var(--empty,);
  --tw-backdrop-hue-rotate: var(--empty,);
  --tw-backdrop-invert: var(--empty,);
  --tw-backdrop-opacity: var(--empty,);
  --tw-backdrop-saturate: var(--empty,);
  --tw-backdrop-sepia: var(--empty,);
}

// actual tailwind css
// we're not using the tailwind reset, didn't like how it setup ::placeholder color
@tailwind components;
@tailwind utilities;

@mixin theme-light {
  // theme-light
  --color-background: white;

  --color-primary: #e37857;

  // Button
  // Normal
  // default
  --color-normal-default-bg: var(--color-background);
  --color-normal-default-text: var(--color-text);
  --color-normal-default-border: var(--color-border);
  // &:hover
  --color-normal-hover-bg: #{darken(white, 5%)};
  --color-normal-hover-text: var(--color-normal-default-text);
  --color-normal-hover-border: var(--color-normal-default-border);
  // &:active
  --color-normal-active-bg: #{darken(white, 10%)};
  --color-normal-active-text: var(--color-normal-default-text);
  --color-normal-active-border: var(--color-normal-default-border);
  // &:disabled
  --color-normal-disabled-bg: var(--color-normal-default-bg);
  --color-normal-disabled-text: var(--color-normal-default-border);
  --color-normal-disabled-border: var(--color-normal-default-border);
  // Primary
  // default
  --color-primary-default-bg: #d97706;
  --color-primary-default-text: white;
  --color-primary-default-border: var(--color-primary-default-bg);
  // &:hover
  --color-primary-hover-bg: #{darken(#d97706, 5%)};
  --color-primary-hover-text: var(--color-primary-default-text);
  --color-primary-hover-border: var(--color-primary-default-border);
  // &:active
  --color-primary-active-bg: #{darken(#d97706, 10%)};
  --color-primary-active-text: var(--color-primary-default-text);
  --color-primary-active-border: var(--color-primary-default-border);
  // &:disabled
  --color-primary-disabled-bg: var(--color-primary-active-bg);
  --color-primary-disabled-text: var(--color-primary-default-text);
  --color-primary-disabled-border: var(--color-primary-default-border);
  // Danger
  --color-danger-default-bg: #dc2626;
  --color-danger-default-text: #fff2f1;
  --color-danger-default-border: var(--color-danger-default-bg);
  // &:hover
  --color-danger-hover-bg: #{lighten(#dc2626, 7%)};
  --color-danger-hover-text: var(--color-danger-default-text);
  --color-danger-hover-border: var(--color-danger-default-border);
  // &:active
  --color-danger-active-bg: #{darken(#dc2626, 5%)};
  --color-danger-active-text: #ffdad7;
  --color-danger-active-border: var(--color-danger-default-border);
  // &:disabled
  --color-danger-disabled-bg: #{darken(#ef4444, 20%)};
  --color-danger-disabled-text: var(--color-danger-default-text);
  --color-danger-disabled-border: var(--color-danger-default-border);

  --color-text: #4a4a4a;
  --color-text-muted: rgba(115, 115, 115, 1);

  --color-input-placeholder: rgb(151, 151, 154);

  --color-link: var(--color-text);
  --color-link-hover: var(--color-link);

  --color-border: #dbdbdb;
  --color-border-selected-day: var(--color-border);

  --color-accent: rgb(255, 114, 71);

  --color-danger: #ff3860;

  --color-background-card: var(--color-background);
  --color-background-calendar-day: whitesmoke;
  --color-background-calendar-item: var(--color-background);
  --color-background-empty-image: rgb(227, 227, 227);
  --color-modal-background: rgba(10, 10, 10, 0.57);
}

.theme-light {
  @include theme-light();
}

@mixin theme-dark {
  --color-background: #000000;
  --color-primary: #d97706;
  --color-danger: #dc2626;

  // Button
  // Normal
  // default
  --color-normal-default-bg: #161b22;
  --color-normal-default-text: var(--color-text);
  --color-normal-default-border: var(--color-border);
  // &:hover
  --color-normal-hover-bg: #{lighten(#161b22, 5%)};
  --color-normal-hover-text: var(--color-text);
  --color-normal-hover-border: var(--color-normal-default-border);
  // &:active
  --color-normal-active-bg: #{lighten(#161b22, 7%)};
  --color-normal-active-text: var(--color-text);
  --color-normal-active-border: #{lighten(#2f353c, 5%)};
  // &:disabled
  --color-normal-disabled-bg: #{darken(#161b22, 2%)};
  --color-normal-disabled-text: rgb(114, 120, 128);
  --color-normal-disabled-border: var(--color-normal-default-border);
  // Primary
  // default
  --color-primary-default-bg: #d97706;
  --color-primary-default-text: white;
  --color-primary-default-border: var(--color-primary-default-bg);
  // &:hover
  --color-primary-hover-bg: #{darken(#d97706, 5%)};
  --color-primary-hover-text: var(--color-primary-default-text);
  --color-primary-hover-border: var(--color-primary-default-border);
  // &:active
  --color-primary-active-bg: #{darken(#d97706, 10%)};
  --color-primary-active-text: var(--color-primary-default-text);
  --color-primary-active-border: var(--color-primary-default-border);
  // &:disabled
  --color-primary-disabled-bg: var(--color-primary-active-bg);
  --color-primary-disabled-text: var(--color-primary-default-text);
  --color-primary-disabled-border: var(--color-primary-default-border);
  // Danger
  --color-danger-default-bg: #dc2626;
  --color-danger-default-text: #fff2f1;
  --color-danger-default-border: var(--color-danger-default-bg);
  // &:hover
  --color-danger-hover-bg: #{lighten(#dc2626, 7%)};
  --color-danger-hover-text: var(--color-danger-default-text);
  --color-danger-hover-border: var(--color-danger-default-border);
  // &:active
  --color-danger-active-bg: #{darken(#dc2626, 5%)};
  --color-danger-active-text: #ffdad7;
  --color-danger-active-border: var(--color-danger-default-border);
  // &:disabled
  --color-danger-disabled-bg: #{darken(#ef4444, 20%)};
  --color-danger-disabled-text: var(--color-danger-default-text);
  --color-danger-disabled-border: var(--color-danger-default-border);

  --color-text: rgb(230, 237, 243);
  --color-text-muted: var(--color-text);

  --color-input-placeholder: rgb(151, 151, 154);

  --color-border: rgb(38, 38, 38);
  --color-border-selected-day: var(--color-border);
  --color-accent: var(--color-text);

  --color-background-card: #0f1011;
  --color-background-calendar-day: var(--color-background-card);
  --color-background-calendar-item: var(--color-background-card);
  --color-background-empty-image: #382e27;
  --color-modal-background: rgba(10, 10, 10, 0.57);
}

.theme-dark {
  @include theme-dark();
}

@mixin theme-dark-dimmed {
  --color-background: rgb(28, 33, 40);

  --color-primary: #854d0e;
  --color-primary-button-text: white;
  // Button
  // Normal
  // default
  --color-normal-default-bg: rgb(28, 33, 40);
  --color-normal-default-text: var(--color-text);
  --color-normal-default-border: var(--color-border);
  // &:hover
  --color-normal-hover-bg: #{lighten(rgb(28, 33, 40), 5%)};
  --color-normal-hover-text: var(--color-text);
  --color-normal-hover-border: var(--color-normal-default-border);
  // &:active
  --color-normal-active-bg: #{lighten(rgb(28, 33, 40), 7%)};
  --color-normal-active-text: var(--color-text);
  --color-normal-active-border: var(--color-border);
  // &:disabled
  --color-normal-disabled-bg: #{darken(rgb(28, 33, 40), 2%)};
  --color-normal-disabled-text: rgb(114, 120, 128);
  --color-normal-disabled-border: var(--color-normal-default-border);
  // Primary
  // default
  --color-primary-default-bg: #d97706;
  --color-primary-default-text: white;
  --color-primary-default-border: var(--color-primary-default-bg);
  // &:hover
  --color-primary-hover-bg: #{darken(#d97706, 5%)};
  --color-primary-hover-text: var(--color-primary-default-text);
  --color-primary-hover-border: var(--color-primary-default-border);
  // &:active
  --color-primary-active-bg: #{darken(#d97706, 10%)};
  --color-primary-active-text: var(--color-primary-default-text);
  --color-primary-active-border: var(--color-primary-default-border);
  // &:disabled
  --color-primary-disabled-bg: var(--color-primary-active-bg);
  --color-primary-disabled-text: var(--color-primary-default-text);
  --color-primary-disabled-border: var(--color-primary-default-border);
  // Danger
  --color-danger-default-bg: #dc2626;
  --color-danger-default-text: #fff2f1;
  --color-danger-default-border: var(--color-danger-default-bg);
  // &:hover
  --color-danger-hover-bg: #{lighten(#dc2626, 7%)};
  --color-danger-hover-text: var(--color-danger-default-text);
  --color-danger-hover-border: var(--color-danger-default-border);
  // &:active
  --color-danger-active-bg: #{darken(#dc2626, 5%)};
  --color-danger-active-text: #ffdad7;
  --color-danger-active-border: var(--color-danger-default-border);
  // &:disabled
  --color-danger-disabled-bg: #{darken(#ef4444, 20%)};
  --color-danger-disabled-text: var(--color-danger-default-text);
  --color-danger-disabled-border: var(--color-danger-default-border);

  --color-text: rgb(173, 186, 199);
  --color-text-muted: var(--color-text);

  --color-input-placeholder: rgb(151, 151, 154);

  --color-border: rgb(68, 76, 86);
  --color-border-selected-day: var(--color-background);
  --color-accent: var(--color-text);

  --color-danger: #fb5274;

  --color-background-card: rgb(34, 39, 46);
  --color-background-calendar-day: var(--color-background-card);
  --color-background-calendar-item: var(--color-background);
  --color-background-empty-image: #382e27;
  --color-modal-background: rgba(10, 10, 10, 0.57);
}

.theme-dark-dimmed {
  @include theme-dark-dimmed();
}

@mixin theme-autumn {
  --color-background: rgb(185, 110, 52);

  --color-primary: #e37857;
  --color-primary-button-text: white;

  // Button
  // Normal
  // default
  --color-normal-default-bg: var(--color-background);
  --color-normal-default-text: var(--color-text);
  --color-normal-default-border: var(--color-border);
  // &:hover
  --color-normal-hover-bg: #{darken(rgb(185, 110, 52), 3%)};
  --color-normal-hover-text: var(--color-normal-default-text);
  --color-normal-hover-border: var(--color-normal-default-border);
  // &:active
  --color-normal-active-bg: #{darken(rgb(185, 110, 52), 7%)};
  --color-normal-active-text: var(--color-normal-default-text);
  --color-normal-active-border: var(--color-normal-default-border);
  // &:disabled
  --color-normal-disabled-bg: var(--color-normal-default-bg);
  --color-normal-disabled-text: var(--color-normal-default-border);
  --color-normal-disabled-border: var(--color-normal-default-border);
  // Primary
  // default
  --color-primary-default-bg: #d97706;
  --color-primary-default-text: white;
  --color-primary-default-border: var(--color-primary-default-bg);
  // &:hover
  --color-primary-hover-bg: #{darken(#d97706, 5%)};
  --color-primary-hover-text: var(--color-primary-default-text);
  --color-primary-hover-border: var(--color-primary-default-border);
  // &:active
  --color-primary-active-bg: #{darken(#d97706, 10%)};
  --color-primary-active-text: var(--color-primary-default-text);
  --color-primary-active-border: var(--color-primary-default-border);
  // &:disabled
  --color-primary-disabled-bg: var(--color-primary-active-bg);
  --color-primary-disabled-text: var(--color-primary-default-text);
  --color-primary-disabled-border: var(--color-primary-default-border);
  // Danger
  --color-danger-default-bg: #dc2626;
  --color-danger-default-text: #fff2f1;
  --color-danger-default-border: var(--color-danger-default-bg);
  // &:hover
  --color-danger-hover-bg: #{lighten(#dc2626, 7%)};
  --color-danger-hover-text: var(--color-danger-default-text);
  --color-danger-hover-border: var(--color-danger-default-border);
  // &:active
  --color-danger-active-bg: #{darken(#dc2626, 5%)};
  --color-danger-active-text: #ffdad7;
  --color-danger-active-border: var(--color-danger-default-border);
  // &:disabled
  --color-danger-disabled-bg: #{darken(#ef4444, 20%)};
  --color-danger-disabled-text: var(--color-danger-default-text);
  --color-danger-disabled-border: var(--color-danger-default-border);

  --color-text: rgb(237, 237, 237);
  --color-text-muted: var(--color-text);

  --color-input-placeholder: rgb(208, 197, 180);

  --color-border: #dbdbdb;
  --color-border-selected-day: var(--color-accent);
  --color-accent: hsla(15, 50%, 82%, 0.865);

  --color-danger: #fb5274;

  --color-background-card: rgb(188, 133, 92);
  --color-background-calendar-day: var(--color-background-card);
  --color-background-calendar-item: rgb(188, 133, 92);
  --color-background-empty-image: var(--color-accent);
  --color-modal-background: rgba(10, 10, 10, 0.57);
}

.theme-autumn {
  @include theme-autumn();
}

@mixin theme-solarized {
  --color-background: #fdf6e3;

  --color-primary: #b58900;
  --color-primary-button-text: white;

  // Button
  // Normal
  // default
  --color-normal-default-bg: var(--color-background);
  --color-normal-default-text: var(--color-text);
  --color-normal-default-border: var(--color-border);
  // &:hover
  --color-normal-hover-bg: #{darken(#fdf6e3, 3%)};
  --color-normal-hover-text: var(--color-normal-default-text);
  --color-normal-hover-border: var(--color-normal-default-border);
  // &:active
  --color-normal-active-bg: #{darken(#fdf6e3, 5%)};
  --color-normal-active-text: var(--color-normal-default-text);
  --color-normal-active-border: var(--color-normal-default-border);
  // &:disabled
  --color-normal-disabled-bg: var(--color-normal-default-bg);
  --color-normal-disabled-text: var(--color-normal-default-border);
  --color-normal-disabled-border: var(--color-normal-default-border);
  // Primary
  // default
  --color-primary-default-bg: #d97706;
  --color-primary-default-text: white;
  --color-primary-default-border: var(--color-primary-default-bg);
  // &:hover
  --color-primary-hover-bg: #{darken(#d97706, 5%)};
  --color-primary-hover-text: var(--color-primary-default-text);
  --color-primary-hover-border: var(--color-primary-default-border);
  // &:active
  --color-primary-active-bg: #{darken(#d97706, 10%)};
  --color-primary-active-text: var(--color-primary-default-text);
  --color-primary-active-border: var(--color-primary-default-border);
  // &:disabled
  --color-primary-disabled-bg: var(--color-primary-active-bg);
  --color-primary-disabled-text: var(--color-primary-default-text);
  --color-primary-disabled-border: var(--color-primary-default-border);
  // Danger
  --color-danger-default-bg: #dc2626;
  --color-danger-default-text: #fff2f1;
  --color-danger-default-border: var(--color-danger-default-bg);
  // &:hover
  --color-danger-hover-bg: #{lighten(#dc2626, 7%)};
  --color-danger-hover-text: var(--color-danger-default-text);
  --color-danger-hover-border: var(--color-danger-default-border);
  // &:active
  --color-danger-active-bg: #{darken(#dc2626, 5%)};
  --color-danger-active-text: #ffdad7;
  --color-danger-active-border: var(--color-danger-default-border);
  // &:disabled
  --color-danger-disabled-bg: #{darken(#ef4444, 20%)};
  --color-danger-disabled-text: var(--color-danger-default-text);
  --color-danger-disabled-border: var(--color-danger-default-border);

  --color-text: #4a4a4a;
  --color-text-muted: rgba(115, 115, 115, 1);

  --color-input-placeholder: #b1b1b1;

  --color-border: #93a1a1;
  --color-border-selected-day: var(--color-background-calendar-day);
  --color-accent: #657b83;

  --color-danger: #dc322f;

  --color-background-card: var(--color-background);
  --color-background-calendar-day: #eee8d5;
  --color-background-calendar-item: var(--color-background);
  --color-background-empty-image: var(--color-accent);
  --color-modal-background: rgba(10, 10, 10, 0.57);
}

.theme-solarized {
  @include theme-solarized();
}

// hide native chrome pickers
input::-webkit-calendar-picker-indicator {
  display: none;
}

input[type="date"]::-webkit-input-placeholder {
  visibility: hidden !important;
}

@media (prefers-color-scheme: dark) {
  .night\:theme-light {
    @include theme-light();
  }
  .night\:theme-dark {
    @include theme-dark();
  }
  .night\:theme-dark-dimmed {
    @include theme-dark-dimmed();
  }
  .night\:theme-autumn {
    @include theme-autumn();
  }
  .night\:theme-solarized {
    @include theme-solarized();
  }
}
@media (prefers-color-scheme: light) {
  .day\:theme-light {
    @include theme-light();
  }
  .day\:theme-dark {
    @include theme-dark();
  }
  .day\:theme-dark-dimmed {
    @include theme-dark-dimmed();
  }
  .day\:theme-autumn {
    @include theme-autumn();
  }
  .day\:theme-solarized {
    @include theme-solarized();
  }
}
